import { RouteObject } from '@rocketlane/rocket-router';
import { ReactNode, lazy } from 'react';
import { PermissionSetType } from 'api/models';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useNewDashboardEnabled } from 'hooks/use-feature';
import { DashboardWidgetWrapper } from 'routes/route-navigators';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { retryImportInsideRoutesDefinition } from 'utils/lazyWithRetry';

const DashboardById = lazy(() =>
  import('./dashboard-by-id').then((m) => ({ default: m.DashboardById })),
);
const AddWidgetPageV2 = lazy(() =>
  import('./widget-creation-steps-v2/add-widget-v2').then((m) => ({
    default: m.AddWidgetPageV2,
  })),
);
const EditWidgetPageV2 = lazy(() =>
  import('./widget-creation-steps-v2/edit-widget-v2').then((m) => ({
    default: m.EditWidgetPageV2,
  })),
);
const AddWidgetPage = lazy(() =>
  import('./widgets/add-widget').then((m) => ({ default: m.AddWidgetPage })),
);
const EditWidgetPage = lazy(() =>
  import('./widgets/edit-widget').then((m) => ({ default: m.EditWidgetPage })),
);

// Component to handle conditional widget rendering
const AddWidgetWrapper = () => {
  const isNewDashboardEnabled = useNewDashboardEnabled();

  if (isNewDashboardEnabled) {
    return <AddWidgetPageV2 />;
  }

  return <AddWidgetPage />;
};

const EditWidgetWrapper = () => {
  const isNewDashboardEnabled = useNewDashboardEnabled();

  if (isNewDashboardEnabled) {
    return <EditWidgetPageV2 />;
  }

  return <EditWidgetPage />;
};

const DashboardPageWrapper = ({ children }: { children: ReactNode }) => {
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isSuperUser =
    currentUserPermissionSetType === PermissionSetType.SuperUser;

  // @TODO RBAC: Remove this after RBAC is shipped
  const icertisPmAccountRole =
    currentUserPermissionSetType === PermissionSetType.IcertisPmAccountRole;

  return (
    <SecureRoute
      rule={
        isAccountAdmin || isSuperUser || icertisPmAccountRole
          ? undefined
          : 'DASHBOARD:READ'
      }
    >
      {children}
    </SecureRoute>
  );
};

export const dashboardRoutes: RouteObject = {
  path: ROUTE_PATHS.DASHBOARDS,
  async lazy() {
    const { DashboardReportPage } = await retryImportInsideRoutesDefinition(
      () => import('./dashboard'),
    );

    return {
      element: (
        <DashboardPageWrapper>
          <DashboardReportPage />
        </DashboardPageWrapper>
      ),
    };
  },
  children: [
    {
      path: ROUTE_PATHS.DASHBOARD_ID,
      element: <DashboardById />,
      caseSensitive: true,
      children: [
        {
          path: ROUTE_PATHS.DASHBOARD_WIDGETS,
          element: <DashboardWidgetWrapper />,
          children: [
            {
              path: ROUTE_PATHS.NEW_DASHBOARD_WIDGET,
              element: <AddWidgetWrapper />,
              caseSensitive: true,
            },
            {
              path: `${ROUTE_PATHS.DASHBOARD_WIDGETS_ID}/${ROUTE_PATHS.EDIT_DASHBOARD_WIDGET}`,
              element: <EditWidgetWrapper />,
              caseSensitive: true,
            },
          ],
        },
      ],
    },
  ],
};
